"use client";
import { useState, useRef, useEffect } from "react";
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import Header from "../../../components/admin/common/Header";
import PageWrapper from "../../../components/admin/common/PageWrapper";
import Sidebar from "../../../components/admin/common/Sidebar";
import {
  getStudentsByStatus,
  updateLoginAuthStatus,
  deleteStudentMembership,
} from "../../../services/studentServices";
import DropdownSelect from "../../../components/admin/common/DropDownSelect";
import { Pagination } from "../../../components/admin/common/PaginationNumber";
import StudentDetailsModal from "../../../components/admin/students/StudentDetailsModal";
import { useNavigate } from "react-router";
import { toast } from "react-hot-toast";
import DeleteConfirmationModal from "../../../components/admin/members/DeleteMemberModal";
import { ChevronDown, ChevronUp, Check, X, Filter, Plus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { getCookie } from "../../../utils";
import { useUserModulePermissions } from "../../../hooks/useUserModulePermissions";

// Custom Select Component (inline)
const CustomSelect = ({
  label,
  value,
  onChange,
  options,
  placeholder = "Select",
  required = false,
  isClearable = true,
  error = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const dropdownVariants = {
    hidden: { opacity: 0, y: -10, scaleY: 0.95, originY: 0 },
    visible: {
      opacity: 1,
      y: 0,
      scaleY: 1,
      originY: 0,
      transition: { duration: 0.2, ease: "easeOut" },
    },
    exit: {
      opacity: 0,
      y: -5,
      scaleY: 0.95,
      originY: 0,
      transition: { duration: 0.15, ease: "easeIn" },
    },
  };

  const toggleDropdown = (e) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (option, e) => {
    e.stopPropagation();
    onChange(option);
    setIsOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleDropdownClick = (e) => {
      e.stopPropagation();
    };

    const dropdown = dropdownRef.current;
    if (dropdown) {
      dropdown.addEventListener("mousedown", handleDropdownClick);
      return () => {
        dropdown.removeEventListener("mousedown", handleDropdownClick);
      };
    }
  }, []);

  return (
    <div className="mt-1" ref={dropdownRef}>
      {label && (
        <label className="text-sm font-medium text-black mb-2 block">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <div className="relative mb-3">
        <button
          onClick={toggleDropdown}
          type="button"
          className={`flex items-center justify-between w-full h-10 px-3 text-sm bg-white border rounded-lg hover:border-gray-400 focus:outline-none ${
            error ? "border-red-500" : "border-gray-300"
          }`}
        >
          <div className="flex items-center gap-2 font-medium text-black">
            <span>
              {value?.label || (
                <span className="text-grey font-medium">{placeholder}</span>
              )}
            </span>
            {value && isClearable && (
              <X
                size={16}
                className="text-black mt-1 cursor-pointer"
                onClick={handleClear}
              />
            )}
          </div>
          {isOpen ? (
            <ChevronUp size={18} className="text-black" />
          ) : (
            <ChevronDown size={18} className="text-black" />
          )}
        </button>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={dropdownVariants}
              className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 max-h-60 overflow-auto origin-top"
            >
              <ul className="py-1">
                {options.map((option) => (
                  <li
                    key={option.value}
                    onClick={(e) => handleOptionClick(option, e)}
                    className={`flex items-center justify-between font-normal px-3 py-2 text-sm cursor-pointer ${
                      value?.value === option.value
                        ? "bg-[#4B00E0] text-white"
                        : "hover:bg-gray-100 text-black"
                    }`}
                  >
                    <span>{option.label}</span>
                    {value?.value === option.value && <Check size={16} />}
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {error && <p className="text-red font-semibold text-sm mt-1">{error}</p>}
    </div>
  );
};

const StudentList = () => {
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [entriesOption, setEntriesOption] = useState({
    value: 10,
    label: "10",
  });
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  const [filters, setFilters] = useState({
    status: null,
    loginAuthStatus: null,
    search: "",
    email: "",
    mobile: "",
    minAge: "",
    maxAge: "",
    institutionName: "",
    location: "",
    degree: "",
    fieldOfStudy: "",
    yearOfStart: "",
    targetedYearOfCompletion: "",
  });

  const [appliedFilters, setAppliedFilters] = useState(filters);

  const limit = entriesOption.value;

  const handleInputChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value,
    });
  };

  // Get readable filter labels
  const getFilterLabel = (key, value) => {
    const labels = {
      status: "Status",
      loginAuthStatus: "Login Status",
      search: "Name",
      email: "Email",
      mobile: "Mobile",
      minAge: "Min Age",
      maxAge: "Max Age",
      institutionName: "Institution",
      location: "Location",
      degree: "Degree",
      fieldOfStudy: "Field",
      yearOfStart: "Start Year",
      targetedYearOfCompletion: "Completion Year",
    };

    const displayValue =
      typeof value === "object" && value !== null ? value.label : value;

    return `${labels[key]}: ${displayValue}`;
  };

  // Get list of active filters with readable labels
  const getActiveFilters = () => {
    return Object.entries(appliedFilters)
      .filter(([key, value]) => {
        if (value === null || value === "" || value === undefined) return false;
        if (typeof value === "object" && value !== null && "value" in value) {
          return (
            value.value !== null &&
            value.value !== undefined &&
            value.value !== ""
          );
        }
        return true;
      })
      .map(([key, value]) => ({
        key,
        label: getFilterLabel(key, value),
        value,
      }));
  };

  const activeFilters = getActiveFilters();
  const activeFilterCount = activeFilters.length;

  // Build query params from applied filters
  const buildQueryParams = () => {
    const params = {
      page,
      limit,
    };

    Object.entries(appliedFilters).forEach(([key, value]) => {
      if (value === null || value === undefined || value === "") {
        return;
      }

      if (typeof value === "object" && value !== null && "value" in value) {
        if (
          value.value !== null &&
          value.value !== undefined &&
          value.value !== ""
        ) {
          params[key] = value.value;
        }
      } else {
        params[key] = value;
      }
    });

    return params;
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["students", appliedFilters, page, limit],
    queryFn: () => getStudentsByStatus(buildQueryParams()),
    placeholderData: keepPreviousData,
    staleTime: 30000,
  });

  const students = data?.data?.students || [];
  const totalPages = data?.data?.totalPages || 1;
  const total = data?.data?.total || 0;

  // Mutation for updating login status
  const updateStatusMutation = useMutation({
    mutationFn: ({ id, loginAuthStatus }) =>
      updateLoginAuthStatus(id, loginAuthStatus),
    onMutate: async ({ id, loginAuthStatus }) => {
      await queryClient.cancelQueries({
        queryKey: ["students", appliedFilters, page, limit],
      });

      const previousData = queryClient.getQueryData([
        "students",
        appliedFilters,
        page,
        limit,
      ]);

      queryClient.setQueryData(
        ["students", appliedFilters, page, limit],
        (old) => {
          if (!old?.data?.students) return old;
          return {
            ...old,
            data: {
              ...old.data,
              students: old.data.students.map((student) =>
                student._id === id ? { ...student, loginAuthStatus } : student
              ),
            },
          };
        }
      );

      return { previousData };
    },
    onError: (err, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          ["students", appliedFilters, page, limit],
          context.previousData
        );
      }
      toast.error(err.message || "Failed to update login status");
    },
    onSuccess: () => {
      toast.success("Login status updated successfully");
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["students", appliedFilters, page, limit],
      });
    },
  });

  // Mutation for deleting student
  const deleteMutation = useMutation({
    mutationFn: (id) => deleteStudentMembership(id),
    onSuccess: () => {
      toast.success("Student deleted successfully");
      queryClient.invalidateQueries({
        queryKey: ["students", appliedFilters, page, limit],
      });
      setIsDeleteModalOpen(false);
      setStudentToDelete(null);
    },
    onError: (err) => {
      toast.error(err.message || "Failed to delete student");
    },
  });

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleEntriesChange = (option) => {
    setEntriesOption(option);
    setPage(1);
  };

  const handleViewStudent = (student) => {
    setSelectedStudent(student);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedStudent(null), 300);
  };

  const handleStatusToggle = (student) => {
    const newStatus =
      student.loginAuthStatus === "active" ? "inactive" : "active";
    updateStatusMutation.mutate({
      id: student._id,
      loginAuthStatus: newStatus,
    });
  };

  const handleDeleteClick = (student) => {
    setStudentToDelete(student);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (studentToDelete) {
      deleteMutation.mutate(studentToDelete._id);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setTimeout(() => setStudentToDelete(null), 300);
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    setPage(1);
    toast.success("Filters applied successfully");
  };

  const handleClearFilters = () => {
    const emptyFilters = {
      status: null,
      loginAuthStatus: null,
      search: "",
      email: "",
      mobile: "",
      minAge: "",
      maxAge: "",
      institutionName: "",
      location: "",
      degree: "",
      fieldOfStudy: "",
      yearOfStart: "",
      targetedYearOfCompletion: "",
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setPage(1);
    toast.success("Filters cleared");
  };

  const handleRemoveFilter = (filterKey) => {
    const updatedFilters = {
      ...appliedFilters,
      [filterKey]: typeof appliedFilters[filterKey] === "object" ? null : "",
    };
    setFilters(updatedFilters);
    setAppliedFilters(updatedFilters);
    setPage(1);
    toast.success("Filter removed");
  };

  const startIndex = (page - 1) * limit;

  const role = getCookie("role");
  const userId = getCookie("userId");

  const { data: permissionsData } = useUserModulePermissions(
    "Students",
    userId,
    {
      enabled: role === "maintainer" && userId && userId !== "undefined",
    }
  );

  let canEdit = false;
  let canDelete = false;

  if (role === "admin") {
    canEdit = true;
    canDelete = true;
  } else if (role === "maintainer" && permissionsData?.data?.permissions) {
    const modulePermissions = permissionsData?.data?.permissions.find(
      (p) => p.module === "Students"
    );

    if (modulePermissions) {
      canEdit = modulePermissions.edit === true;
      canDelete = modulePermissions.delete === true;
    }
  }

  return (
    <div className="flex h-screen p-4 gap-4 bg-white">
      <div className="flex-shrink-0">
        <Sidebar />
      </div>

      <div className="flex-1 h-full bg-[#F3EFFF] rounded-[27px] overflow-hidden">
        <PageWrapper>
          <div className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto">
              <div className="p-4">
                <Header
                  title="Student List"
                  desc="Review, approve, or reject student registered applications"
                />

                {/* Entries Dropdown and Filter Toggle */}
                <div className="flex items-center justify-between gap-2 mb-4 mt-6">
                  <div className="w-20">
                    <DropdownSelect
                      selected={entriesOption}
                      onChange={handleEntriesChange}
                      options={[
                        { value: 10, label: "10" },
                        { value: 25, label: "25" },
                        { value: 50, label: "50" },
                        { value: 100, label: "100" },
                      ]}
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    {canEdit && (
                      <button
                        onClick={() => navigate(`/admin/members/students/add`)}
                        className="flex items-center gap-2 px-4 py-2 bg-[#4B00E0] text-white rounded-lg hover:bg-[#3D00B8] transition-colors text-sm font-medium shadow-md"
                      >
                        <Plus size={16} />
                        Add Student
                      </button>
                    )}
                    <button
                      onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                      className="flex items-center gap-2 px-4 py-2 bg-[#4B00E0] text-white rounded-lg hover:bg-[#3D00B8] transition-colors text-sm font-medium shadow-md"
                    >
                      <Filter size={16} />
                      <span>Filters</span>
                      {activeFilterCount > 0 && (
                        <span className="bg-white text-[#4B00E0] px-2 py-0.5 rounded-full text-xs font-bold">
                          {activeFilterCount}
                        </span>
                      )}
                      {isFilterExpanded ? (
                        <ChevronUp size={18} />
                      ) : (
                        <ChevronDown size={18} />
                      )}
                    </button>
                  </div>
                </div>

                {/* Active Filters Display */}
                {activeFilterCount > 0 && (
                  <div className="mb-4 bg-white rounded-lg shadow-md p-4 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                        <div className="w-1 h-5 bg-[#4B00E0] rounded"></div>
                        Active Filters ({activeFilterCount})
                      </h3>
                      <button
                        onClick={handleClearFilters}
                        className="text-xs text-[#4B00E0] hover:text-[#3D00B8] font-medium"
                      >
                        Clear All
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {activeFilters.map((filter) => (
                        <div
                          key={filter.key}
                          className="inline-flex items-center gap-2 bg-[#4B00E0] text-white px-3 py-1.5 rounded-full text-xs font-medium"
                        >
                          <span>{filter.label}</span>
                          <button
                            onClick={() => handleRemoveFilter(filter.key)}
                            className="hover:bg-white hover:text-[#4B00E0] rounded-full p-0.5 transition-colors"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Collapsible Filter Section */}
                <AnimatePresence>
                  {isFilterExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden mb-4"
                    >
                      <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
                        {/* Basic Filters */}
                        <div className="mb-4">
                          <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
                            <div className="w-1 h-5 bg-[#4B00E0] rounded"></div>
                            Basic Filters
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-black mb-1 mt-1">
                                Search by Name
                              </label>
                              <input
                                type="text"
                                placeholder="Enter full name"
                                value={filters.search}
                                onChange={(e) =>
                                  handleInputChange("search", e.target.value)
                                }
                                className="w-full px-3 py-2 h-10 mt-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4B00E0] focus:border-transparent"
                              />
                            </div>

                            <CustomSelect
                              label="Membership Status"
                              value={filters.status}
                              onChange={(value) =>
                                handleInputChange("status", value)
                              }
                              options={[
                                { value: "pending", label: "Pending" },
                                { value: "approved", label: "Approved" },
                                { value: "rejected", label: "Rejected" },
                                { value: "expired", label: "Expired" },
                              ]}
                              placeholder="All Status"
                            />

                            <CustomSelect
                              label="Login Auth Status"
                              value={filters.loginAuthStatus}
                              onChange={(value) =>
                                handleInputChange("loginAuthStatus", value)
                              }
                              options={[
                                { value: "active", label: "Active" },
                                { value: "inactive", label: "Inactive" },
                              ]}
                              placeholder="All Status"
                            />

                            <div>
                              <label className="block text-sm font-medium text-black mb-1 mt-1">
                                Email
                              </label>
                              <input
                                type="email"
                                placeholder="Enter email"
                                value={filters.email}
                                onChange={(e) =>
                                  handleInputChange("email", e.target.value)
                                }
                                className="w-full px-3 py-2 h-10 mt-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4B00E0] focus:border-transparent"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-black mb-1">
                                Mobile
                              </label>
                              <input
                                type="text"
                                placeholder="Enter mobile number"
                                value={filters.mobile}
                                onChange={(e) =>
                                  handleInputChange("mobile", e.target.value)
                                }
                                className="w-full px-3 py-2 h-10 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4B00E0] focus:border-transparent"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-black mb-1">
                                Min Age
                              </label>
                              <input
                                type="number"
                                placeholder="Minimum age"
                                value={filters.minAge}
                                onChange={(e) =>
                                  handleInputChange("minAge", e.target.value)
                                }
                                className="w-full px-3 py-2 h-10 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4B00E0] focus:border-transparent"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-black mb-1">
                                Max Age
                              </label>
                              <input
                                type="number"
                                placeholder="Maximum age"
                                value={filters.maxAge}
                                onChange={(e) =>
                                  handleInputChange("maxAge", e.target.value)
                                }
                                className="w-full px-3 py-2 h-10 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4B00E0] focus:border-transparent"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Institution Filters */}
                        <div className="mb-4">
                          <h3 className="text-sm font-bold text-black mb-3 flex items-center gap-2">
                            <div className="w-1 h-5 bg-[#4B00E0] rounded"></div>
                            Institution Details
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-black mb-1">
                                Institution Name
                              </label>
                              <input
                                type="text"
                                placeholder="Search institution"
                                value={filters.institutionName}
                                onChange={(e) =>
                                  handleInputChange(
                                    "institutionName",
                                    e.target.value
                                  )
                                }
                                className="w-full px-3 py-2 h-10 mt-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4B00E0] focus:border-transparent"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-black mb-1">
                                Location
                              </label>
                              <input
                                type="text"
                                placeholder="Search location"
                                value={filters.location}
                                onChange={(e) =>
                                  handleInputChange("location", e.target.value)
                                }
                                className="w-full px-3 py-2 h-10 mt-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4B00E0] focus:border-transparent"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-black mb-1">
                                Degree
                              </label>
                              <input
                                type="text"
                                placeholder="Search degree"
                                value={filters.degree}
                                onChange={(e) =>
                                  handleInputChange("degree", e.target.value)
                                }
                                className="w-full px-3 py-2 h-10 mt-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4B00E0] focus:border-transparent"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-black mb-1">
                                Field of Study
                              </label>
                              <input
                                type="text"
                                placeholder="Search field"
                                value={filters.fieldOfStudy}
                                onChange={(e) =>
                                  handleInputChange(
                                    "fieldOfStudy",
                                    e.target.value
                                  )
                                }
                                className="w-full px-3 py-2 h-10 mt-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4B00E0] focus:border-transparent"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-black mb-1">
                                Year of Start
                              </label>
                              <input
                                type="number"
                                placeholder="Year"
                                value={filters.yearOfStart}
                                onChange={(e) =>
                                  handleInputChange(
                                    "yearOfStart",
                                    e.target.value
                                  )
                                }
                                className="w-full px-3 py-2 h-10 mt-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4B00E0] focus:border-transparent"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-black mb-1">
                                Year of Completion
                              </label>
                              <input
                                type="number"
                                placeholder="Year"
                                value={filters.targetedYearOfCompletion}
                                onChange={(e) =>
                                  handleInputChange(
                                    "targetedYearOfCompletion",
                                    e.target.value
                                  )
                                }
                                className="w-full px-3 py-2 h-10 mt-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4B00E0] focus:border-transparent"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-3 pt-3 border-t border-gray-200">
                          <button
                            onClick={handleApplyFilters}
                            className="px-6 py-2 bg-[#4B00E0] text-white rounded-lg hover:bg-[#3D00B8] transition-colors font-medium text-sm shadow-md"
                          >
                            Apply Filters
                          </button>
                          <button
                            onClick={handleClearFilters}
                            className="px-6 py-2 bg-white text-[#4B00E0] border-2 border-[#4B00E0] rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
                          >
                            Clear All
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {isLoading && (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                    <p className="ml-4 text-gray-600 text-sm">
                      Loading students...
                    </p>
                  </div>
                )}

                {isError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                    <p className="font-semibold text-sm">
                      Error loading students
                    </p>
                    <p className="text-xs">
                      {error?.message || "Failed to fetch students"}
                    </p>
                  </div>
                )}

                {!isLoading && !isError && (
                  <>
                    <div className="bg-white rounded-lg shadow">
                      <div className="overflow-x-hidden rounded-t-xl">
                        <table className="w-full text-xs border border-gray-300">
                          <thead className="bg-[#4B00E0] text-white text-sm">
                            <tr>
                              <th className="py-3 px-3 text-left font-bold border-r border-gray-400 w-12">
                                No
                              </th>
                              <th className="py-3 px-3 text-left font-bold border-r border-gray-400 w-[180px]">
                                Full Name
                              </th>
                              <th className="py-3 px-3 text-left font-bold border-r border-gray-400 w-[160px]">
                                Email
                              </th>
                              <th className="py-3 px-3 text-left font-bold border-r border-gray-400 w-[110px]">
                                Mobile
                              </th>
                              <th className="py-3 px-3 text-left font-bold border-r border-gray-400 w-[140px]">
                                Institution
                              </th>
                              <th className="py-3 px-3 text-left font-bold border-r border-gray-400 w-[80px]">
                                Degree
                              </th>
                              <th className="py-3 px-3 text-left font-bold border-r border-gray-400 w-[100px]">
                                Applied Date
                              </th>
                              <th className="py-3 px-3 text-left font-bold border-r border-gray-400 w-[100px]">
                                Login Status
                              </th>
                              <th className="py-3 px-3 text-left font-bold w-[180px]">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {students.length === 0 ? (
                              <tr>
                                <td
                                  colSpan={9}
                                  className="text-center text-gray-500 py-6 border-t border-gray-300 text-sm"
                                >
                                  No students found with current filters.
                                </td>
                              </tr>
                            ) : (
                              students.map((student, idx) => (
                                <tr
                                  key={student._id}
                                  className="border-t border-gray-300 text-xs hover:bg-gray-50 transition-colors"
                                >
                                  <td className="px-3 py-3 font-normal text-black border-r border-gray-300">
                                    {startIndex + idx + 1}
                                  </td>
                                  <td className="px-3 py-3 font-normal text-black border-r border-gray-300">
                                    <div className="flex items-center gap-2">
                                      {student.photo && (
                                        <img
                                          src={student.photo}
                                          alt={student.fullName}
                                          className="w-7 h-7 rounded-full object-cover flex-shrink-0"
                                        />
                                      )}
                                      <span className="font-semibold break-words">
                                        {student.fullName}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-3 py-3 font-normal text-black border-r border-gray-300 break-all">
                                    {student.email}
                                  </td>
                                  <td className="px-3 py-3 font-normal text-black border-r border-gray-300">
                                    {student.mobile}
                                  </td>
                                  <td className="px-3 py-3 font-normal text-black border-r border-gray-300 break-words">
                                    {student.institutionDetails?.[0]
                                      ?.institutionName || "N/A"}
                                  </td>
                                  <td className="px-3 py-3 font-normal text-black border-r border-gray-300 break-words">
                                    {student.institutionDetails?.[0]?.degree ||
                                      "N/A"}
                                  </td>
                                  <td className="px-3 py-3 text-black border-r border-gray-300 whitespace-nowrap">
                                    {new Date(
                                      student.createdAt
                                    ).toLocaleDateString("en-IN")}
                                  </td>
                                  <td className="px-3 py-3 text-black border-r border-gray-300">
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() =>
                                          handleStatusToggle(student)
                                        }
                                        disabled={
                                          updateStatusMutation.isLoading
                                        }
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                                          student.loginAuthStatus === "active"
                                            ? "bg-green"
                                            : "bg-red"
                                        }`}
                                        title={`Current status: ${
                                          student.loginAuthStatus || "inactive"
                                        }`}
                                      >
                                        <span
                                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                            student.loginAuthStatus === "active"
                                              ? "translate-x-6"
                                              : "translate-x-1"
                                          }`}
                                        />
                                      </button>
                                      <span
                                        className={`text-xs font-medium ${
                                          student.loginAuthStatus === "active"
                                            ? "text-green"
                                            : "text-red"
                                        }`}
                                      >
                                        {student.loginAuthStatus === "active"
                                          ? "Active"
                                          : "Inactive"}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-3 py-3">
                                    <div className="flex flex-wrap items-center gap-1.5">
                                      <button
                                        onClick={() =>
                                          handleViewStudent(student)
                                        }
                                        className="px-2.5 py-1.5 bg-[#4f22d8] text-white rounded transition-colors text-[11px] font-medium whitespace-nowrap"
                                      >
                                        View
                                      </button>

                                      {canEdit && (
                                        <button
                                          onClick={() =>
                                            navigate(
                                              `/admin/members/students/edit/${student._id}`
                                            )
                                          }
                                          className="px-2.5 py-1.5 bg-green text-white rounded hover:bg-green-700 transition-colors text-[11px] font-medium whitespace-nowrap"
                                        >
                                          Edit
                                        </button>
                                      )}

                                      {canDelete && (
                                        <button
                                          onClick={() =>
                                            handleDeleteClick(student)
                                          }
                                          disabled={deleteMutation.isLoading}
                                          className="px-2.5 py-1.5 bg-red text-white rounded hover:bg-red-700 transition-colors text-[11px] font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                          Delete
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Pagination */}
                    {students.length > 0 && (
                      <div className="mt-4">
                        <Pagination
                          totalPages={totalPages}
                          currentPage={page}
                          onPageChange={handlePageChange}
                          showPages={5}
                        />
                      </div>
                    )}

                    {/* Showing results text */}
                    {students.length > 0 && (
                      <div className="text-center text-xs text-gray-600 mt-2">
                        Showing {startIndex + 1} to{" "}
                        {Math.min(startIndex + students.length, total)} of{" "}
                        {total} entries
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </PageWrapper>
      </div>

      <StudentDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        student={selectedStudent}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onRequestClose={handleDeleteCancel}
        onDelete={handleDeleteConfirm}
        itemName={studentToDelete?.fullName || ""}
        itemType="student"
      />
    </div>
  );
};

export default StudentList;
